import React from 'react';
import { head } from 'lodash';
import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import { ThemeProps } from '@theme/base';
import { mq } from '@styles/breakpoints';
import { SectionTitleSizes } from '@styles/layout';
import { DestinationFaqItem } from '@state/destination-faq';
import ExpandableListWrapper from '@components/material-ui/expandable-list-item/ExpandableListWrapper';
import { useI18NextContext } from '@components/hooks';
import { TelLink } from '@components/common/tel-link';

export interface DestinationFAQProps {
  faqs: Array<DestinationFaqItem>;
  testId?: string;
  titleSize?: SectionTitleSizes;
}

export interface DestinationFaqListItem {
  title: string;
  text: string;
}

export const getFaqItems = (faqs) => {
  const items: Array<DestinationFaqListItem> = Object.entries(faqs).map(([key, value]: any) => ({
    key,
    title: value.title,
    text: value.description
  }));

  return items;
};

const Container = styled.div(({ theme }: ThemeProps) => ({
  background: theme.custom.colors.group10.lighter,
  borderRadius: 10,
  padding: theme.custom.spacing.large,
  h2: {
    lineHeight: 1.2,
    margin: 0,
    padding: `0 ${theme.custom.spacing.large}px`,
    [mq.small]: {
      padding: 0
    }
  },
  h6: {
    marginBottom: 0
  },
  a: {
    color: 'inherit'
  },
  ['.hide-mobile']: {
    display: 'none',
    [mq.small]: {
      display: 'block'
    }
  },
  [mq.small]: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    padding: theme.custom.spacing.large * 2
  }
}));

const TextWrapper = styled.div(({ theme }: ThemeProps) => ({
  padding: theme.custom.spacing.large,
  textAlign: 'center',
  [mq.small]: {
    padding: theme.custom.spacing.large,
    paddingRight: theme.custom.spacing.large * 4,
    textAlign: 'left'
  }
}));

const DestinationFAQ = withTheme((props: DestinationFAQProps) => {
  const t: any = useI18NextContext();
  const { faqs, testId, titleSize } = props;
  if (!faqs) {
    return null;
  }
  const destination = head(faqs.flatMap((faq) => faq.destination_name)) || '';

  return (
    <Container>
      <TextWrapper>
        <h2>{t('destination__faq--title', { destination })}</h2>
        <p>{t('destination__faq--legend', { destination })}</p>
        <hr className="hide-mobile" />
        <h6 className="hide-mobile">{t('destination__faq--here-to-help')}</h6>
        <p className="hide-mobile">
          {t('destination__faq--cta', { destination })} <TelLink phoneNumber={t('common__brand_telephone')} />
        </p>
      </TextWrapper>
      <ExpandableListWrapper items={getFaqItems(faqs)} testId={testId} titleSize={titleSize} />
    </Container>
  );
});

export default DestinationFAQ;
